export class AddressAutocomplete {
    /**
     * prefix: id prefix of the address fields; `customer` finds customerPostalCode, customerCity,
     * customerStreet, customerHouseNumber and customer-street-suggestions. An empty prefix finds
     * postalCode, city, street, houseNumber and street-suggestions.
     * isActive: false while the API does not cover the address (OpenPLZ: Germany only); the fields
     * then stay plain inputs. Call release() when it turns false.
     */
    constructor(prefix, { isActive = () => true } = {}) {
        const id = (name) => prefix ? prefix + name : name[0].toLowerCase() + name.slice(1);
        this.prefix = prefix;
        this.isActive = isActive;
        this.apiFailed = false;
        this.postalInput = document.getElementById(id('PostalCode'));
        this.cityInput = document.getElementById(id('City'));
        this.streetInput = document.getElementById(id('Street'));
        this.houseInput = document.getElementById(id('HouseNumber'));
        this.streetSuggestions = document.getElementById(prefix ? `${prefix}-street-suggestions` : 'street-suggestions');

        // NEW - use existing error divs
        this.postalStatus = this.postalInput.parentElement.querySelector('.error');
        this.cityStatus = this.cityInput.parentElement.querySelector('.error');
        this.streetStatus = this.streetInput.parentElement.querySelector('.error');
        this.houseStatus = this.houseInput.parentElement.querySelector('.error');

        this.openplzUrl = 'https://openplzapi.org/de';
        this.currentStreetSuggestions = [];
        this.highlightedIndex = -1;
        this.searchTimeout = null;

        this.validatedData = {
            postal: null,
            city: null,
            street: null,
            house: null,
            isValidated: false

        };

        this.init();
    }

    init() {
        // Postal code validation
        this.postalInput.addEventListener('input', (e) => {
            if (this.isActive()) this.handlePostalInput(e.target.value);
        });

        this.postalInput.addEventListener('blur', (e) => {
            if (this.isActive()) this.validatePostalCode(e.target.value);
        });

        // Street autocomplete; after an API failure the address is typed without suggestions
        this.streetInput.addEventListener('input', (e) => {
            if (this.isActive() && !this.apiFailed) this.handleStreetInput(e.target.value);
        });

        this.streetInput.addEventListener('keydown', (e) => {
            this.handleStreetKeyNavigation(e);
        });

        // this.streetInput.addEventListener('blur', () => {
        //     setTimeout(() => this.hideStreetSuggestions(), 150);
        // });

        // House number validation
        let houseNumberTimeout = null;
        this.houseInput.addEventListener('input', (e) => {
            if (houseNumberTimeout) {
                clearTimeout(houseNumberTimeout);
            }
            houseNumberTimeout = setTimeout(() => {
                if (this.isActive() && !this.apiFailed) this.validateHouseNumber(e.target.value);
            }, 500);
        });
        this.streetSuggestions.addEventListener('click', (e) => {
            e.stopPropagation(); // Prevents event from bubbling up to document
        });
        // Close suggestions when clicking outside
        document.addEventListener('click', (e) => {
            if (!e.target.closest('.autocomplete-container')) {
                this.hideStreetSuggestions();
            }
        });
    }

    updateValidationState() {
        this.validatedData.isValidated = !!(
            this.validatedData.postal &&
            this.validatedData.city &&
            this.validatedData.street &&
            this.validatedData.house !== null
        );
    }

    handlePostalInput(value) {
        this.apiFailed = false;
        // Reset dependent fields if postal code changes
        if (value.length < 5) {
            this.resetDependentFields();
        }

        // Auto-validate when 5 digits entered
        if (value.length === 5) {
            this.validatePostalCode(value);
        } else {
            this.setFieldStatus(this.postalInput, this.postalStatus, 'info', '5-stellige Postleitzahl eingeben');
        }
    }

    async validatePostalCode(postalCode) {
        if (!/^\d{5}$/.test(postalCode)) {
            this.setFieldStatus(this.postalInput, this.postalStatus, 'error', 'Ungültiges Postleitzahlenformat');
            return;
        }

        this.setFieldStatus(this.postalInput, this.postalStatus, 'info', 'Wird überprüft...');
        try {
            // Use OpenPLZ API to get localities for postal code
            const response = await fetch(
                `${this.openplzUrl}/Localities?postalCode=${postalCode}`,
                { headers: { 'accept': 'application/json' } }
            );

            if (!response.ok) throw new Error('API request failed');

            const data = await response.json();

            if (!data || data.length === 0) {
                this.setFieldStatus(this.postalInput, this.postalStatus, 'error', 'Postleitzahl nicht gefunden');
                return;
            }

            // Get unique cities for this postal code
            const cities = [...new Set(data.map(item => item.name))].sort();

            if (cities.length === 1) {
                // Single city - auto-fill it
                const city = cities[0];
                this.cityInput.value = city;
                this.validatedData.postal = postalCode;
                this.validatedData.city = city;

                this.setFieldStatus(this.postalInput, this.postalStatus, 'success', '✓ Gültige Postleitzahl');
                this.setFieldStatus(this.cityInput, this.cityStatus, 'success', `✓ Automatisch ausgefüllt: ${city}`);

                // Enable street input
                this.streetInput.disabled = false;
                this.cityInput.disabled = false;
                this.houseInput.disabled = false;
                this.streetInput.focus();

            } else {
                // Multiple cities - let user choose
                this.cityInput.readOnly = false;
                this.cityInput.value = cities[0]; // Set first as default
                this.validatedData.postal = postalCode;
                this.validatedData.city = cities;

                this.setFieldStatus(this.postalInput, this.postalStatus, 'success', '✓ Gültige Postleitzahl');
                this.setFieldStatus(this.cityInput, this.cityStatus, 'success',
                    `✓ Auto-filled: ${cities[0]} (${cities.length} cities available)`);

                // Create dropdown for city selection if needed
                this.createCityDropdown(cities);

                // Enable street input
                this.streetInput.disabled = false;
                this.cityInput.disabled = false;
            }


        } catch (error) {
            console.error('Postal validation error:', error);
            // Without the API the address can still be typed in.
            this.release();
            this.apiFailed = true;
            this.setFieldStatus(this.postalInput, this.postalStatus, 'info', 'Prüfung nicht möglich, bitte Adresse selbst eingeben');
        }
    }

    /** Makes every address field editable again and clears suggestions and status messages. */
    release() {
        this.cityInput.readOnly = false;
        this.streetInput.disabled = false;
        this.houseInput.disabled = false;
        this.currentStreetSuggestions = [];
        this.hideStreetSuggestions();
        this.setFieldStatus(this.postalInput, this.postalStatus, '', '');
        this.setFieldStatus(this.cityInput, this.cityStatus, '', '');
        this.setFieldStatus(this.streetInput, this.streetStatus, '', '');
        this.setFieldStatus(this.houseInput, this.houseStatus, '', '');
    }

    createCityDropdown(cities) {
        // Simple approach - add a small note showing available cities
        if (cities.length > 1) {
            const otherCities = cities.slice(1).join(', ');
            this.setFieldStatus(this.cityInput, this.cityStatus, 'info',
                `Andere Städte: ${otherCities}`);
        }
    }

    // Step 2: Street autocomplete using OpenPLZ
    handleStreetInput(value) {
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }

        this.highlightedIndex = -1;
        if (!this.validatedData.postal || !this.validatedData.city) {
            this.setFieldStatus(this.streetInput, this.streetStatus, 'error', 'Geben Sie zuerst die Postleitzahl ein');
            return;
        }

        if (value.length < 3) {
            this.hideStreetSuggestions();
            this.setFieldStatus(this.streetInput, this.streetStatus, 'info', 'Geben Sie den Straßennamen ein...');
            return;
        }

        this.showStreetLoading();

        this.searchTimeout = setTimeout(() => {
            this.searchStreets(value);
        }, 500);
    }

    async searchStreets(streetName) {
        try {
            // Search for streets using OpenPLZ API with postal code and locality filters
            const response = await fetch(
                `${this.openplzUrl}/Streets?name=${encodeURIComponent(streetName)}&postalCode=${this.validatedData.postal}&locality=${encodeURIComponent(this.validatedData.city)}`,
                { headers: { 'accept': 'application/json' } }
            );

            if (!response.ok) throw new Error('Street search failed');

            const data = await response.json();

            if (!data || data.length === 0) {
                // Try broader search without exact locality match
                const fallbackResponse = await fetch(
                    `${this.openplzUrl}/Streets?name=${encodeURIComponent(streetName)}&postalCode=${this.validatedData.postal}`,
                    { headers: { 'accept': 'application/json' } }
                );

                if (fallbackResponse.ok) {
                    const fallbackData = await fallbackResponse.json();
                    if (fallbackData && fallbackData.length > 0) {
                        this.processStreetResults(fallbackData);
                    } else {
                        // No results found - offer manual entry
                        this.displayManualEntryOption(streetName);
                    }
                } else {
                    // Show manual entry option
                    this.displayManualEntryOption(streetName);
                }
                return;
            }

            this.processStreetResults(data);

        } catch (error) {
            console.error('Street search error:', error);
            this.setFieldStatus(this.streetInput, this.streetStatus, 'error', 'Stra en-Suche fehlgeschlagen');
        }
    }

    processStreetResults(streets) {
        // Remove duplicates and sort
        const uniqueStreets = streets
            .filter((street, index, self) =>
                index === self.findIndex(s => s.name === street.name && s.locality === street.locality)
            )
            .sort((a, b) => a.name.localeCompare(b.name))
            .slice(0, 8); // Limit results

        this.currentStreetSuggestions = uniqueStreets.map(street => ({
            name: street.name,
            display: street.name,
            secondary: `${street.postalCode} ${street.locality}`,
            data: street
        }));

        this.displayStreetSuggestions(this.currentStreetSuggestions);
    }

    displayManualEntryOption(streetName) {
        this.currentStreetSuggestions = [{
            name: streetName,
            display: `${streetName}`,
            secondary: 'Keine Ergebnisse – klicken Sie, um die Straße manuell einzugeben',
            isManualEntry: true
        }];

        this.displayStreetSuggestions(this.currentStreetSuggestions);
        // this.setFieldStatus(this.streetInput, this.streetStatus, 'info', 'Nichts gefunden – Sie können die Straße selbst eingeben');
    }

    displayStreetSuggestions(streets) {
        if (streets.length === 0) {
            this.streetSuggestions.innerHTML = '<div class="no-results">Keine Straßen gefunden</div>';
            this.streetSuggestions.style.display = 'block';
            return;
        }

        const html = streets.map((street, index) => `
            <div class="suggestion-item" data-index="${index}">
                <div class="suggestion-main">${this.escapeHtml(street.display)}</div>
                <div class="suggestion-secondary">${this.escapeHtml(street.secondary)}</div>
            </div>
        `).join('');

        this.streetSuggestions.innerHTML = html;

        this.streetSuggestions.querySelectorAll('.suggestion-item').forEach((item, index) => {
            item.addEventListener('click', () => {
                this.selectStreet(index);
            });
        });

        this.streetSuggestions.style.display = 'block';
    }

    selectStreet(index) {
        if (index < 0 || index >= this.currentStreetSuggestions.length) return;

        const street = this.currentStreetSuggestions[index];
        this.streetInput.value = street.name;
        this.validatedData.street = street.name;

        // Handle manual entry (no data object)
        if (street.isManualEntry) {
            this.setFieldStatus(this.streetInput, this.streetStatus, 'success', 'Straße manuell eingegeben');
        } else if (street.data) {
            // Handle API result with data
            if (street.data.locality !== this.validatedData.city) {
                this.cityInput.value = street.data.locality;
                this.validatedData.city = street.data.locality;
                this.setFieldStatus(this.cityInput, this.cityStatus, 'success',
                    `✓ Aktualisiert auf: ${street.data.locality}`);
            }
            this.setFieldStatus(this.streetInput, this.streetStatus, 'success', 'Straße erfolgreich ausgewählt');
        }

        this.hideStreetSuggestions();

        this.houseInput.disabled = false;
        this.houseInput.focus();

    }

    handleStreetKeyNavigation(e) {
        if (!this.currentStreetSuggestions.length) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                this.highlightedIndex = Math.min(this.highlightedIndex + 1, this.currentStreetSuggestions.length - 1);
                this.updateStreetHighlight();
                break;
            case 'ArrowUp':
                e.preventDefault();
                this.highlightedIndex = Math.max(this.highlightedIndex - 1, -1);
                this.updateStreetHighlight();
                break;
            case 'Enter':
                e.preventDefault();
                if (this.highlightedIndex >= 0) {
                    this.selectStreet(this.highlightedIndex);
                }
                break;
            case 'Escape':
                this.hideStreetSuggestions();
                break;
        }
    }

    updateStreetHighlight() {
        const items = this.streetSuggestions.querySelectorAll('.suggestion-item');
        items.forEach((item, index) => {
            item.classList.toggle('highlighted', index === this.highlightedIndex);
        });
    }

    // Updated house number validation method
    async validateHouseNumber(value) {
        if (!value.trim()) {
            this.setFieldStatus(this.houseInput, this.houseStatus, 'error');
            this.validatedData.house = '';
            return;
        }

        const houseNumber = value.trim();

        // Basic format validation first
        if (!/^[1-9]\d*(?:[ -]?(?:[a-zA-Z]+|[1-9]\d*))?$/.test(houseNumber)) {
            this.setFieldStatus(this.houseInput, this.houseStatus, 'error', 'Ungültiges Format'); this.validatedData.house = null;;
            return;
        }

        // Show loading state
        this.setFieldStatus(this.houseInput, this.houseStatus, 'info', 'Hausnummer wird überprüft...');

        this.setFieldStatus(this.houseInput, this.houseStatus, 'success', `Format gültig`);
        this.validatedData.house = houseNumber;

        this.hideStreetSuggestions();
        this.updateValidationState();
    }

    // Final validation using OpenPLZ (basic check)
    async validateCompleteAddress() {
        if (!this.isFormComplete()) return;

        this.result.textContent = 'Adresse wird validiert...';
        this.result.className = 'result-display info';

        try {
            // Verify the street still exists in the postal code
            const response = await fetch(
                `${this.openplzUrl}/Streets?name=${encodeURIComponent(this.validatedData.street)}&postalCode=${this.validatedData.postal}&locality=${encodeURIComponent(this.validatedData.city)}`,
                { headers: { 'accept': 'application/json' } }
            );

            if (response.ok) {
                const data = await response.json();

                if (data && data.length > 0) {
                    const fullAddress = `${this.validatedData.street} ${this.validatedData.house || ''}, ${this.validatedData.postal} ${this.validatedData.city}`.trim().replace(', ,', ',');

                    this.result.innerHTML = `
                        <strong>✓ Adresse erfolgreich validiert!</strong><br>
                        <strong>Adresse:</strong> ${fullAddress}<br>
                        <strong>Verifiziert:</strong> Straße vorhanden im Postleitzahlen-Datenbank
                    `;
                    this.result.className = 'result-display success';

                    // Dispatch custom event
                    document.dispatchEvent(new CustomEvent('addressValidated', {
                        detail: {
                            ...this.validatedData,
                            fullAddress
                        }
                    }));
                } else {
                    this.result.textContent = '⚠️ Straße nicht gefunden im Postleitzahlen-Bereich';
                    this.result.className = 'result-display error';
                }
            } else {
                throw new Error('Validation request failed');
            }

        } catch (error) {
            console.error('Final validation error:', error);
            this.result.textContent = '❌ Finale Validierung fehlgeschlagen';
            this.result.className = 'result-display error';
        }
    }


    setFieldStatus(input, statusEl, type, message) {
        // Clear existing classes
        input.classList.remove('valid', 'invalid', 'error');
        statusEl.className = type; // Reset to base class

        // Set message
        statusEl.textContent = message;

        // Add appropriate styling
        if (type === 'success') {
            input.classList.add('valid');
        } else if (type === 'error') {
            input.classList.add('error');
        } else {
            statusEl.style.color = ''; // Reset color
        }
    }

    resetDependentFields() {
        this.cityInput.value = '';
        this.streetInput.value = '';
        this.houseInput.value = '';

        this.cityInput.readOnly = true;
        this.streetInput.disabled = true;
        this.houseInput.disabled = true;

        this.setFieldStatus(this.cityInput, this.cityStatus, '', '');
        this.setFieldStatus(this.streetInput, this.streetStatus, '', '');
        this.setFieldStatus(this.houseInput, this.houseStatus, '', '');

        this.hideStreetSuggestions();

        this.validatedData = { postal: null, city: null, street: null, house: null };
    }

    isFormComplete() {
        return this.validatedData.postal && this.validatedData.city && this.validatedData.street;
    }

    showStreetLoading() {
        this.streetSuggestions.innerHTML = '<div class="loading">Suche Straßen...</div>';
        this.streetSuggestions.style.display = 'block';
    }

    hideStreetSuggestions() {
        this.streetSuggestions.style.display = 'none';
        this.highlightedIndex = -1;
    }

    clearForm() {
        this.postalInput.value = '';
        this.resetDependentFields();
        this.setFieldStatus(this.postalInput, this.postalStatus, '', '');
    }
    resetAllFields() {
        this.clearForm();
        this.validatedData = { postal: null, city: null, street: null, house: null };
    }
    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }
}